import React, { useEffect, useState } from 'react';
import { SurveyData, User, UserRole, DynamicQuestion, SurveySection } from '../types';
import { getSurveys, getUsers, deleteSurvey, getDynamicQuestions, getDynamicSections, getSurveyIICId, getHouseholdName, getSurveyIICStatus, getMemberName, getMemberPhoto, getHouseholdPhone, getHouseholdSerialNo, getCategoryForAge, getMemberAge, getCategoryName, getMergedRepeaterMembers, extractStudentsFromSurvey } from '../services/storage';
import { Download, Search, Bus, Filter, Users, CheckCircle, Trophy, Loader, Trash2, Edit, Image as ImageIcon, Eye, X, Calendar, User as UserIcon, Sparkles } from 'lucide-react';
import SecureActionModal from './SecureActionModal';
import SurveyForm from './SurveyForm';
import { useToast } from './Toast';
import { formatSerialNoForDisplay } from '../utils/cardUtils';
// @ts-ignore
import JSZip from 'jszip';

interface DashboardProps {
  user: User;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [data, setData] = useState<SurveyData[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingStudents, setIsExportingStudents] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [filterWard, setFilterWard] = useState('');
  
  // Edit State
  const [editingSurvey, setEditingSurvey] = useState<SurveyData | null>(null);

  // View Details Modal State
  const [viewingSurveyDetails, setViewingSurveyDetails] = useState<SurveyData | null>(null);

  // View Photo State
  const [viewPhotos, setViewPhotos] = useState<string[]>([]);
  const [viewPhotoIndex, setViewPhotoIndex] = useState(0);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<string | null>(null);
  const { addToast } = useToast();

  const isSectionVisible = (secId: string) => {
    if (user.role === 'MASTER_ADMIN') return true;
    return (user.visibleSections || []).includes(secId);
  };

  // Function to refresh data
  const loadData = async () => {
    setLoading(true);
    try {
      const [surveysData, usersData, qs, secs] = await Promise.all([getSurveys(), getUsers(), getDynamicQuestions(), getDynamicSections()]);
      setData(surveysData);
      setUsers(usersData);
      setQuestions(qs);
      setSections(secs);
    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEditSuccess = () => {
    setEditingSurvey(null);
    addToast("Survey updated successfully", "success");
    loadData(); // Refresh list after edit
  };

  // HYBRID PHOTO FINDER (Explicit + Recursive)
  const getAllPhotosFromSurvey = (survey: SurveyData): string[] => {
      const uniquePhotos = new Set<string>();
      
      // 1. Explicit Check based on Question Definition (Most reliable)
      const imageQs = questions.filter(q => q.type === 'image');
      imageQs.forEach(q => {
          // Check nested in section
          if (survey[q.section] && survey[q.section][q.id]) {
              const val = survey[q.section][q.id];
              if (typeof val === 'string' && val.startsWith('data:image')) uniquePhotos.add(val);
          }
          // Check flat/root
          if (survey[q.id]) {
             const val = survey[q.id];
             if (typeof val === 'string' && val.startsWith('data:image')) uniquePhotos.add(val);
          }
      });

      // 2. Recursive Check (Catch-all for dynamic fields)
      const extract = (obj: any) => {
        if (!obj) return;
        if (typeof obj === 'string') {
            if (obj.startsWith('data:image')) uniquePhotos.add(obj);
            return;
        }
        if (Array.isArray(obj)) {
            obj.forEach(extract);
            return;
        }
        if (typeof obj === 'object') {
            // Avoid recursing into huge non-data objects if any
            Object.values(obj).forEach(extract);
        }
      };
      
      // Only extract from data sections to save performance
      sections.forEach(sec => {
          if (survey[sec.id]) extract(survey[sec.id]);
      });
      
      return Array.from(uniquePhotos);
  };

  const getStudentsForSurvey = (survey: SurveyData): any[] => {
    return extractStudentsFromSurvey(survey, questions);
  };

  // Stats Logic
  const totalFilled = data.length;
  
  const repStats = users.reduce((acc, user) => {
    if (user.role === 'REPRESENTATIVE' || user.role === 'ADMIN' || user.role === 'MASTER_ADMIN') {
        const count = data.filter(s => s.submittedBy === user.id).length;
        if (count > 0 || user.role === 'REPRESENTATIVE') {
            acc.push({ name: user.name, count, role: user.role });
        }
    }
    return acc;
  }, [] as {name: string, count: number, role: string}[]).sort((a, b) => b.count - a.count);

  const matchesUniversalSearch = (survey: SurveyData, term: string) => {
    if (!term) return true;
    const t = term.toLowerCase().trim();

    // 1. Extract all students and do a prioritized search
    const students = extractStudentsFromSurvey(survey, questions);
    
    // Priority 1: Parent phone number search
    const hasPhoneMatch = students.some(s => s.phone && String(s.phone).toLowerCase().includes(t));
    if (hasPhoneMatch) return true;

    // Priority 2: Father's / Mother's name search
    const hasParentMatch = students.some(s => s.parentName && s.parentName.toLowerCase().includes(t));
    if (hasParentMatch) return true;

    // Priority 3: Student name search
    const hasStudentMatch = students.some(s => s.studentName && s.studentName.toLowerCase().includes(t));
    if (hasStudentMatch) return true;

    // Fallback: Check all sections recursively
    const checkValue = (val: any): boolean => {
      if (val === undefined || val === null) return false;
      if (typeof val === 'string') {
        if (val.startsWith('data:image')) return false;
        return val.toLowerCase().includes(t);
      }
      if (typeof val === 'number') return String(val).includes(t);
      if (typeof val === 'boolean') return (val ? 'yes' : 'no').includes(t);
      if (Array.isArray(val)) return val.some(checkValue);
      if (typeof val === 'object') return Object.values(val).some(checkValue);
      return false;
    };

    return checkValue(survey);
  };

  const filteredData = data.filter(d => {
    const wardNo = d.wardNo || '';
    const matchesSearch = matchesUniversalSearch(d, search);
    const matchesWard = filterWard === '' || wardNo.includes(filterWard);
    
    return matchesSearch && matchesWard;
  });

  // Student stats and section breakouts
  const allStudents = data.flatMap(survey => getStudentsForSurvey(survey));
  const totalStudentsCount = allStudents.length;

  const filteredStudents = filteredData.flatMap(survey => getStudentsForSurvey(survey));
  const totalFilteredStudentsCount = filteredStudents.length;

  const transportationCount = allStudents.filter(s => s['1782312752155'] === true || String(s['1782312752155']).toLowerCase() === 'yes').length;
  const filteredTransportationCount = filteredStudents.filter(s => s['1782312752155'] === true || String(s['1782312752155']).toLowerCase() === 'yes').length;

  let lsCount = 0;
  let ccCount = 0;
  let flCount = 0;
  allStudents.forEach(s => {
    const age = getMemberAge(s, questions);
    const category = getCategoryForAge(age);
    if (category === 'LS') lsCount++;
    else if (category === 'CC') ccCount++;
    else if (category === 'FL') flCount++;
  });

  let filteredLsCount = 0;
  let filteredCcCount = 0;
  let filteredFlCount = 0;
  filteredStudents.forEach(s => {
    const age = getMemberAge(s, questions);
    const category = getCategoryForAge(age);
    if (category === 'LS') filteredLsCount++;
    else if (category === 'CC') filteredCcCount++;
    else if (category === 'FL') filteredFlCount++;
  });

  const exportStudentsCSV = async () => {
    setIsExportingStudents(true);
    try {
      // 1. Helper to escape CSV
      const escapeCsv = (str: any) => {
          if (str === undefined || str === null) return '';
          const s = String(str);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
              return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
      };

      // 2. Build Headers
      const headers = [
        'Sl No',
        'Student Name',
        'Date of Birth',
        'Gender',
        'School Name',
        'Class',
        'Father\'s Name',
        'Mother\'s Name',
        'Father\'s Phone Number',
        'Mother\'s Phone Number',
        'Parent WhatsApp Number',
        'Parents Email ID',
        'Parent Samajam Member',
        'Parents Samajam Membership ID Number',
        'Hobbies & Interests',
        'Extracurricular Activities',
        'Attended Camps Before',
        'Details of the Camp',
        'Transportation Required',
        'Location'
      ];

      const rows: string[][] = [];

      // 3. Extract and loop over students
      filteredStudents.forEach(s => {
        const slNo = s.serialNo || '';
        const name = s.studentName || '';
        const dob = s['1782312366818'] || s.dob || '';
        const gender = s['1782312394122'] || s.gender || '';
        const schoolName = s['1782312413731'] || s.schoolName || '';
        const className = s['1782312452434'] || s.class || '';
        const fatherName = s['1782311895371'] || s.fatherName || '';
        const motherName = s['1782311923394'] || s.motherName || '';
        const fatherContact = s['1782311944131'] || s.fatherPhone || s.phone || '';
        const motherContact = s['1782311987788'] || s.motherPhone || '';
        const whatsapp = s['1782312038535'] || s.whatsapp || '';
        const email = s['1782312154158'] || s.email || '';
        
        const hasMembershipVal = s['1782312069160'];
        const isMember = hasMembershipVal === true || String(hasMembershipVal).toLowerCase() === 'yes' ? 'Yes' : 'No';
        const membershipId = s['1782312123373'] ? String(s['1782312123373']).trim() : '';
        
        const hobbies = s['1782312504965'] || s.hobbies || '';
        const extracurricular = s['1782312533860'] || s.extracurricular || '';
        
        const attendedCampsVal = s['1782312574481'];
        const attendedCamps = attendedCampsVal === true || String(attendedCampsVal).toLowerCase() === 'yes' ? 'Yes' : 'No';
        const campDetails = s['1782312731027'] || s.campDetails || '';
        
        const transVal = s['1782312752155'];
        const transRequired = transVal === true || String(transVal).toLowerCase() === 'yes' ? 'Yes' : 'No';
        const location = s['1782312786621'] || s.location || '';

        rows.push([
          escapeCsv(slNo),
          escapeCsv(name),
          escapeCsv(dob),
          escapeCsv(gender),
          escapeCsv(schoolName),
          escapeCsv(className),
          escapeCsv(fatherName),
          escapeCsv(motherName),
          escapeCsv(fatherContact),
          escapeCsv(motherContact),
          escapeCsv(whatsapp),
          escapeCsv(email),
          escapeCsv(isMember),
          escapeCsv(membershipId),
          escapeCsv(hobbies),
          escapeCsv(extracurricular),
          escapeCsv(attendedCamps),
          escapeCsv(campDetails),
          escapeCsv(transRequired),
          escapeCsv(location)
        ]);
      });

      const csvContent = "data:text/csv;charset=utf-8," 
        + "\uFEFF" 
        + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `students_directory_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addToast("Student directory export complete!", "success");

    } catch (e) {
      console.error("Student export failed", e);
      addToast("Failed to export students directory", "error");
    } finally {
      setIsExportingStudents(false);
    }
  };

  const exportCSV = async () => {
    setIsExporting(true);
    try {
      // 1. Prepare Sections
      const sortedSections = [...sections].sort((a,b) => a.order - b.order);
      const visibleSections = sortedSections.filter(s => isSectionVisible(s.id));
      
      const singleSections = visibleSections.filter(s => !s.isRepeater);
      const repeaterSections = visibleSections.filter(s => s.isRepeater);

      // 2. Helper to get questions
      const getQs = (secId: string) => questions.filter(q => q.section === secId);

      // 3. Helper to escape CSV
      const escapeCsv = (str: string) => {
          if (str === undefined || str === null) return '';
          const s = String(str);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
              return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
      };

      // 4. Build Headers
      const metaHeaders = ['Survey ID', 'Submitted By', 'Submitted At'];
      let dynamicHeaders: string[] = [];

      singleSections.forEach(s => {
          getQs(s.id).forEach(q => {
              dynamicHeaders.push(`(${s.label}) ${q.label}`);
          });
      });

      // Handle PRIMARY repeater only for flat CSV (usually Family Members)
      const primaryRepeater = repeaterSections.length > 0 ? repeaterSections[0] : null;
      if (primaryRepeater) {
           getQs(primaryRepeater.id).forEach(q => {
              dynamicHeaders.push(`(${primaryRepeater.label}) ${q.label}`);
          });
      }

      const csvHeaders = [...metaHeaders, ...dynamicHeaders].map(escapeCsv);
      const rows: string[][] = [];

      // 5. Build Rows
      filteredData.forEach(survey => {
        const repName = users.find(u => u.id === survey.submittedBy)?.name || 'Unknown';
        const dateStr = survey.submittedAt ? new Date(survey.submittedAt).toLocaleString() : '';
        
        // Base Data (Meta + Single Sections)
        const baseData = [
            escapeCsv(survey.id),
            escapeCsv(repName),
            escapeCsv(dateStr)
        ];

        singleSections.forEach(s => {
            const secData = survey[s.id] || {};
            getQs(s.id).forEach(q => {
                let val = secData[q.id];
                
                // Formatting
                if (val === true) val = 'Yes';
                if (val === false) val = 'No';
                
                let str = val !== undefined && val !== null ? String(val) : '';
                
                if (str.startsWith('data:image')) str = 'Image Saved';
                if (Array.isArray(val)) str = val.join('; ');
                
                baseData.push(escapeCsv(str));
            });
        });

        // Expand Repeater
        if (primaryRepeater) {
             const repeaterItems = survey[primaryRepeater.id];
             if (Array.isArray(repeaterItems) && repeaterItems.length > 0) {
                 repeaterItems.forEach(item => {
                     const itemRow = [...baseData];
                     getQs(primaryRepeater.id).forEach(q => {
                         let val = item[q.id];
                         
                         // Formatting
                         if (val === true) val = 'Yes';
                         if (val === false) val = 'No';

                         let str = val !== undefined && val !== null ? String(val) : '';
                         if (Array.isArray(val)) str = val.join('; ');

                         itemRow.push(escapeCsv(str));
                     });
                     rows.push(itemRow);
                 });
             } else {
                 // Empty row for this survey
                 const emptyCols = getQs(primaryRepeater.id).map(() => '');
                 rows.push([...baseData, ...emptyCols]);
             }
        } else {
             rows.push(baseData);
        }
      });

      const csvContent = "data:text/csv;charset=utf-8," 
        + "\uFEFF" 
        + [csvHeaders.join(','), ...rows.map(e => e.join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `samajam_summer_camp_data_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addToast("Export complete!", "success");

    } catch (e) {
      console.error("Export failed", e);
      addToast("Failed to export data", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAllPhotos = async () => {
    if (filteredData.length === 0) {
        addToast("No surveys found in current list to download.", "info");
        return;
    }

    setIsZipping(true);
    try {
        const zip = new JSZip();
        let count = 0;
        
        // Use a loop to handle async if needed later, but JSZip is sync for adding files
        for (const survey of filteredData) {
            const photos = getAllPhotosFromSurvey(survey);
            if (photos.length > 0) {
                const headName = getHouseholdName(survey, questions) || 'Unknown';
                const serial = getHouseholdSerialNo(survey) || '0000';
                
                photos.forEach((photoData, idx) => {
                    const parts = photoData.split(',');
                    if (parts.length === 2) {
                        const base64Data = parts[1];
                        // Sanitize filename
                        const safeName = headName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20);
                        const filename = `${serial}_${safeName}_${idx + 1}.jpg`;
                        
                        try {
                           zip.file(filename, base64Data, {base64: true});
                           count++;
                        } catch(err) {
                           console.error("Failed to add file to zip", filename, err);
                        }
                    }
                });
            }
        }

        if (count === 0) {
            addToast("No valid photos found in the selected surveys.", "info");
            return; // Exit early
        }

        const content = await zip.generateAsync({type: "blob"});
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `photos_batch_${new Date().toISOString().slice(0,10)}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        addToast(`Downloaded ${count} photos!`, "success");

    } catch (e: any) {
        console.error("Zip Error:", e);
        addToast("Failed to zip photos: " + (e.message || "Unknown error"), "error");
    } finally {
        setIsZipping(false);
    }
  };

  const openPhotoViewer = (photos: string[]) => {
      if (!photos || photos.length === 0) {
        addToast("No photos to view", "info");
        return;
      }
      setViewPhotos(photos);
      setViewPhotoIndex(0);
  };

  const downloadSinglePhoto = (base64: string, name: string) => {
     const link = document.createElement('a');
     link.href = base64;
     link.download = `photo_${name.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
     link.click();
  };

  const initiateDelete = (id: string) => {
    setSurveyToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (surveyToDelete) {
      await deleteSurvey(surveyToDelete);
      addToast("Survey deleted successfully", "success");
      await loadData();
    }
  };

  if (editingSurvey) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center mb-4">
            <button onClick={() => setEditingSurvey(null)} className="mr-4 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 font-bold">
                &larr; Back to Dashboard
            </button>
            <h2 className="text-xl font-bold text-gray-800">Editing Survey: {getHouseholdName(editingSurvey, questions)}</h2>
        </div>
        <SurveyForm user={user} onSuccess={handleEditSuccess} initialData={editingSurvey} />
      </div>
    );
  }

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-600" size={32} /></div>
  }

  // Explicitly define columns to show in the dashboard table
  const displayQuestions: any[] = [
    { id: 'parentAndStudents', label: 'Family (Parent & Students)' },
    { id: 'phone', label: 'Phone Number' },
    { id: 'iicMembership', label: 'Samajam Membership' }
  ];

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Summer Camp Management Dashboard</h2>
        <div className="flex space-x-2 mt-4 md:mt-0">
            <button 
            onClick={downloadAllPhotos} 
            disabled={isZipping}
            className="bg-purple-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-purple-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
            >
            {isZipping ? <Loader className="animate-spin" size={18} /> : <ImageIcon size={18} />}
            <span>{isZipping ? 'Zipping...' : 'Download All Photos (ZIP)'}</span>
            </button>
            <button 
            onClick={exportCSV} 
            disabled={isExporting}
            className="bg-blue-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-blue-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
            >
            {isExporting ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
            <span>{isExporting ? 'Exporting...' : 'Export Full CSV'}</span>
            </button>
            <button 
            onClick={exportStudentsCSV} 
            disabled={isExportingStudents}
            className="bg-emerald-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-emerald-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
            >
            {isExportingStudents ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
            <span>{isExportingStudents ? 'Exporting...' : 'Export Students CSV'}</span>
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {/* Total Families Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-emerald-100 flex items-center space-x-4">
            <div className="bg-emerald-100 p-3 rounded-full text-emerald-600">
                <CheckCircle size={32} />
            </div>
            <div>
                <p className="text-gray-500 text-sm font-semibold">Total Households (Registrations)</p>
                <h3 className="text-3xl font-bold text-gray-800">
                  {totalFilled} {search || filterWard ? <span className="text-sm font-medium text-gray-400">(Filtered: {filteredData.length})</span> : ''}
                </h3>
            </div>
        </div>

        {/* Total Students Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 flex items-center space-x-4">
            <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                <Users size={32} />
            </div>
            <div>
                <p className="text-gray-500 text-sm font-semibold">Total Registered Students</p>
                <h3 className="text-3xl font-bold text-gray-800">
                  {totalStudentsCount} {search || filterWard ? <span className="text-sm font-medium text-gray-400">(Filtered: {totalFilteredStudentsCount})</span> : ''}
                </h3>
            </div>
        </div>

        {/* Transportation Required Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-100 flex flex-col justify-between min-h-[120px]">
            <div className="flex items-center space-x-4">
                <div className="bg-orange-100 p-3 rounded-full text-orange-600">
                    <Bus size={32} />
                </div>
                <div>
                    <p className="text-gray-500 text-sm font-semibold">Transportation Required</p>
                    <h3 className="text-3xl font-bold text-gray-800">
                      {transportationCount} {search || filterWard ? <span className="text-sm font-medium text-gray-400">(Filtered: {filteredTransportationCount})</span> : ''}
                    </h3>
                </div>
            </div>
            {transportationCount > 0 && (
              <div className="mt-4 border-t border-orange-50/80 pt-3 flex items-center justify-between">
                <span className="text-[10px] text-gray-400 font-medium">Download Details</span>
                <button
                  onClick={() => {
                    const targetStudents = (search || filterWard) ? filteredStudents : allStudents;
                    const opted = targetStudents.filter(s => s['1782312752155'] === true || String(s['1782312752155']).toLowerCase() === 'yes');
                    
                    const headers = ['Student Name', 'Parent Name', 'Father\'s Phone Number', 'Mother\'s Phone Number', 'Location'];
                    const csvRows = [
                      headers.join(','),
                      ...opted.map(s => [
                        `"${String(s.studentName || 'N/A').replace(/"/g, '""')}"`,
                        `"${String(s.parentName || 'N/A').replace(/"/g, '""')}"`,
                        `"${String(s.fatherPhone || s.phone || 'N/A').replace(/"/g, '""')}"`,
                        `"${String(s.motherPhone || 'N/A').replace(/"/g, '""')}"`,
                        `"${String(s['1782312786621'] || 'N/A').replace(/"/g, '""')}"`
                      ].join(','))
                    ].join('\n');
                    
                    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.setAttribute("href", url);
                    link.setAttribute("download", `transportation_students_${(search || filterWard) ? 'filtered' : 'all'}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                  className="text-xs font-bold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg px-2.5 py-1 transition flex items-center gap-1 cursor-pointer"
                  title="Download CSV containing transportation student details"
                >
                  <Download size={12} />
                  Download CSV
                </button>
              </div>
            )}
        </div>

        {/* Active Reps Card */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-100 flex items-center space-x-4">
            <div className="bg-purple-100 p-3 rounded-full text-purple-600">
                <Users size={32} />
            </div>
            <div>
                <p className="text-gray-500 text-sm font-semibold">Active Representatives</p>
                <h3 className="text-3xl font-bold text-gray-800">{repStats.filter(r => r.count > 0).length}</h3>
            </div>
        </div>
      </div>

      {/* Section Breakdowns Alert / Stats Grid */}
      <div className="bg-gradient-to-r from-slate-900 to-emerald-950 text-white p-6 rounded-2xl shadow-md border border-slate-800">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h4 className="text-lg font-black tracking-tight flex items-center gap-2">
              <Sparkles className="text-yellow-400" size={20} /> Section-wise Student Counts
            </h4>
            <p className="text-xs text-slate-300">Distribution of students based on age categories</p>
          </div>
          {search || filterWard ? (
            <span className="text-xs font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full border border-emerald-500/30">
              Showing Filtered Counts
            </span>
          ) : null}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {/* Little Stars */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 hover:bg-white/15 transition-all">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-emerald-400 font-bold text-xs uppercase tracking-wider">Little Stars (LS)</p>
                <p className="text-slate-300 text-[10px] mt-0.5">Ages 4 to 8</p>
              </div>
              <span className="text-xs font-bold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded font-mono">LS</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-black text-white">{search || filterWard ? filteredLsCount : lsCount}</span>
              <span className="text-xs text-slate-400 font-bold">students</span>
              {search || filterWard ? <span className="text-xs text-slate-400">(Overall: {lsCount})</span> : null}
            </div>
          </div>

          {/* Creative Champs */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 hover:bg-white/15 transition-all">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-yellow-400 font-bold text-xs uppercase tracking-wider">Creative Champs (CC)</p>
                <p className="text-slate-300 text-[10px] mt-0.5">Ages 9 to 12</p>
              </div>
              <span className="text-xs font-bold bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded font-mono">CC</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-black text-white">{search || filterWard ? filteredCcCount : ccCount}</span>
              <span className="text-xs text-slate-400 font-bold">students</span>
              {search || filterWard ? <span className="text-xs text-slate-400">(Overall: {ccCount})</span> : null}
            </div>
          </div>

          {/* Future Leaders */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 hover:bg-white/15 transition-all">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-cyan-400 font-bold text-xs uppercase tracking-wider">Future Leaders (FL)</p>
                <p className="text-slate-300 text-[10px] mt-0.5">Ages 13 to 21</p>
              </div>
              <span className="text-xs font-bold bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded font-mono">FL</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-black text-white">{search || filterWard ? filteredFlCount : flCount}</span>
              <span className="text-xs text-slate-400 font-bold">students</span>
              {search || filterWard ? <span className="text-xs text-slate-400">(Overall: {flCount})</span> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Data Table */}
        <div className="lg:col-span-2 space-y-4">
            <h3 className="font-bold text-gray-700 text-lg">Registration Entries</h3>
            <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg shadow-sm">
                <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Search by Name or House..." 
                    className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                </div>
                <div className="relative w-full sm:w-48">
                <Filter className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input 
                    type="text" 
                    placeholder="Filter by Ward..." 
                    className="pl-10 p-2 border rounded w-full outline-none focus:ring-2 focus:ring-emerald-500"
                    value={filterWard}
                    onChange={(e) => setFilterWard(e.target.value)}
                />
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl No.</th>
                    {displayQuestions.map(q => (
                        <th key={q.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{q.label}</th>
                    ))}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Photos</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((survey) => {
                        // Dynamically retrieve all photos from the survey object
                        const photos = getAllPhotosFromSurvey(survey);
                        const submittedByName = users.find(u => u.id === survey.submittedBy)?.name || 'Unknown';
                        const submittedDate = survey.submittedAt ? new Date(survey.submittedAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : 'N/A';
                        
                        return (
                        <tr key={survey.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{formatSerialNoForDisplay(getHouseholdSerialNo(survey)) || '-'}</td>
                            {displayQuestions.map(q => {
    let displayVal: React.ReactNode = '-';
    
    if (q.id === 'parentAndStudents') {
      
      let pName = getHouseholdName(survey, questions);
      
      const studentsList = getStudentsForSurvey(survey);
      
      displayVal = (
        <div className="flex flex-col">
          <span className="font-bold text-gray-800 text-sm">{pName || 'Unknown Parent'}</span>
          {studentsList.length > 0 ? (
            <div className="mt-1 flex flex-col space-y-0.5">
              {studentsList.map((s, idx) => {
                const age = getMemberAge(s, questions);
                const cat = getCategoryForAge(age);
                const catName = cat ? getCategoryName(cat) : '';
                return (
                  <span key={idx} className="text-xs text-gray-500 pl-2 border-l-2 border-gray-200">
                    {s.studentName} {catName && <span className="text-gray-400 italic">({catName})</span>}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic mt-1">No students</span>
          )}
        </div>
      );
      
    } else if (q.id === 'phone') {
      const phoneNum = getHouseholdPhone(survey, questions);
      displayVal = phoneNum ? <span className="font-mono text-gray-700">{phoneNum}</span> : '-';
      
    } else if (q.id === 'iicMembership') {
      const isMember = getSurveyIICStatus(survey, questions, sections);
      const membershipNo = getSurveyIICId(survey, questions);
      
      if (isMember) {
        displayVal = (
          <div className="flex flex-col items-start space-y-1">
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
              Member
            </span>
            {membershipNo && (
              <span className="font-mono bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded text-xs font-semibold">
                #{membershipNo}
              </span>
            )}
          </div>
        );
      } else {
        displayVal = (
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
            Not a Member
          </span>
        );
      }
    }

    return (
        <td key={q.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {displayVal}
        </td>
    );
})}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                <div className="flex items-center space-x-1">
                                    <UserIcon size={14} className="text-emerald-500" />
                                    <span>{submittedByName}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                    <Calendar size={14} className="text-blue-400" />
                                    <span>{submittedDate}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {photos.length > 0 ? (
                                <div className="flex space-x-2 items-center">
                                    <button onClick={() => openPhotoViewer(photos)} className="text-blue-600 hover:text-blue-800 underline text-xs flex items-center font-bold bg-blue-50 px-2 py-1 rounded">
                                        <Eye size={12} className="mr-1"/>
                                        View ({photos.length})
                                    </button>
                                </div>
                            ) : <span className="text-gray-300 text-xs">No Photos</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center space-x-2">
                                <button 
                                    onClick={() => setViewingSurveyDetails(survey)}
                                    className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 p-2 rounded transition-colors"
                                    title="View Full Details"
                                >
                                    <Eye size={18} />
                                </button>
                                {(user.role === 'MASTER_ADMIN' || user.role === 'ADMIN') && (
                                <button 
                                    onClick={() => setEditingSurvey(survey)}
                                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded transition-colors"
                                    title="Edit Survey"
                                >
                                    <Edit size={18} />
                                </button>
                                )}
                                {(user.role === 'MASTER_ADMIN') && (
                                <button 
                                    onClick={() => initiateDelete(survey.id)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                                    title="Delete Survey"
                                >
                                    <Trash2 size={18} />
                                </button>
                                )}
                            </td>
                        </tr>
                    )})}
                    {filteredData.length === 0 && (
                    <tr>
                        <td colSpan={displayQuestions.length + 5} className="px-6 py-8 text-center text-gray-500 italic">No records found matching your filters.</td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>
        </div>

        <div className="space-y-4">
            <h3 className="font-bold text-gray-700 text-lg flex items-center"><Trophy className="mr-2 text-yellow-500" size={20}/> Performance</h3>
            <div className="bg-white rounded-lg shadow p-4">
                <ul className="space-y-3">
                    {repStats.map((rep, idx) => (
                        <li key={idx} className="flex justify-between items-center border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                            <div>
                                <p className="font-bold text-gray-800 text-sm">{rep.name}</p>
                                <p className="text-xs text-gray-400 capitalize">{rep.role.toLowerCase()}</p>
                            </div>
                            <div className="bg-emerald-100 text-emerald-700 font-bold px-3 py-1 rounded-full text-xs">
                                {rep.count} Forms
                            </div>
                        </li>
                    ))}
                    {repStats.length === 0 && <p className="text-gray-400 text-sm text-center">No representative data.</p>}
                </ul>
            </div>
        </div>
      </div>

      <SecureActionModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Survey Response"
        message="Are you sure you want to delete this survey response? This action cannot be undone."
      />

      {/* Photo View Modal with Gallery Support */}
      {viewPhotos.length > 0 && (
         <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col items-center justify-center p-4">
            <button className="absolute top-4 right-4 text-white hover:text-red-400 p-2 z-50" onClick={() => setViewPhotos([])}>
                <X size={32} className="bg-gray-800 rounded-full p-1"/>
            </button>
            
            <div className="relative max-w-4xl w-full h-full flex flex-col items-center justify-center">
               <div className="relative">
                   <img src={viewPhotos[viewPhotoIndex]} className="max-w-full max-h-[80vh] rounded shadow-2xl object-contain border-4 border-gray-800" alt="House Proof" />
                   <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                       {viewPhotoIndex + 1} / {viewPhotos.length}
                   </div>
               </div>
               
               <div className="mt-6 flex items-center space-x-6 bg-gray-900 p-4 rounded-xl border border-gray-700 shadow-xl">
                   {viewPhotos.length > 1 && (
                     <div className="flex space-x-4">
                        <button 
                            onClick={() => setViewPhotoIndex((prev) => (prev - 1 + viewPhotos.length) % viewPhotos.length)}
                            className="text-white px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold"
                        >
                            &larr; Prev
                        </button>
                        <button 
                            onClick={() => setViewPhotoIndex((prev) => (prev + 1) % viewPhotos.length)}
                            className="text-white px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold"
                        >
                            Next &rarr;
                        </button>
                     </div>
                   )}
                   <div className="h-8 w-px bg-gray-600 mx-2"></div>
                   <button onClick={() => downloadSinglePhoto(viewPhotos[viewPhotoIndex], `photo_${viewPhotoIndex+1}`)} className="text-emerald-400 flex items-center text-sm font-bold hover:text-emerald-300 hover:bg-gray-800 px-3 py-2 rounded transition-colors">
                       <Download size={18} className="mr-2"/> Download
                   </button>
               </div>
            </div>
         </div>
      )}
      {/* Full Survey Details Modal */}
      {viewingSurveyDetails && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-emerald-600 text-white p-6 flex justify-between items-center flex-shrink-0">
              <div>
                <span className="text-xs uppercase bg-emerald-700 px-2.5 py-1 rounded-full font-bold tracking-wider">
                  Serial No: {formatSerialNoForDisplay(getHouseholdSerialNo(viewingSurveyDetails)) || '-'}
                </span>
                <h3 className="text-2xl font-bold mt-2">Survey Response Details</h3>
              </div>
              <button 
                onClick={() => setViewingSurveyDetails(null)} 
                className="text-white hover:text-red-200 bg-emerald-700 hover:bg-emerald-800 p-2 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content (Scrollable) */}
            <div className="p-6 overflow-y-auto space-y-8">
              {/* Metadata Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                <div>
                  <p className="text-xs text-emerald-800 font-bold uppercase">Submitted By</p>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">
                    {users.find(u => u.id === viewingSurveyDetails.submittedBy)?.name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-emerald-800 font-bold uppercase">Submitted At</p>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">
                    {viewingSurveyDetails.submittedAt ? new Date(viewingSurveyDetails.submittedAt).toLocaleString('en-IN') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-emerald-800 font-bold uppercase">Payment Status</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black uppercase ${
                      viewingSurveyDetails.paymentStatus === 'Paid' ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {viewingSurveyDetails.paymentStatus || 'Pending'}
                    </span>
                    <span className="text-sm font-bold text-gray-700">₹{viewingSurveyDetails.paymentAmount || 0}</span>
                  </div>
                </div>
              </div>

              {/* Loop through sections */}
              {sections.map(sec => {
                const isRepeater = sec.isRepeater;
                if (isRepeater) {
                  const members = viewingSurveyDetails[sec.id] || [];
                  const secQs = questions.filter(q => q.section === sec.id);
                  return (
                    <div key={sec.id} className="space-y-4 border-t border-gray-100 pt-6">
                      <h4 className="text-lg font-black text-emerald-800 flex items-center uppercase tracking-tight">
                        <Users size={20} className="mr-2 text-emerald-600" />
                        {sec.label} ({members.length})
                      </h4>
                      {members.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No members added in this section.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {members.map((m: any, idx: number) => {
                            const photoUrl = getMemberPhoto(m, questions);
                            return (
                              <div key={m.id || idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex space-x-4">
                                {photoUrl && (
                                  <div className="w-20 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-300 flex-shrink-0">
                                    <img 
                                      src={photoUrl} 
                                      alt="Member Photo" 
                                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                      onClick={() => openPhotoViewer([photoUrl])}
                                    />
                                  </div>
                                )}
                                <div className="flex-1 space-y-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-400 font-bold font-mono">#{idx + 1}</span>
                                    {m.idNo && <span className="text-[10px] bg-emerald-100 text-emerald-800 font-black px-1.5 py-0.5 rounded">ID: {m.idNo}</span>}
                                  </div>
                                  {secQs.map(q => {
                                    const val = m[q.id];
                                    if (q.type === 'image' || val === undefined || val === null || val === '') return null;
                                    let displayVal = String(val);
                                    if (q.type === 'yesno') {
                                      displayVal = val === true || val === 'true' || val === 'Yes' ? 'Yes' : 'No';
                                    }
                                    return (
                                      <div key={q.id} className="text-xs">
                                        <span className="text-gray-500 font-medium block">{q.label}:</span>
                                        <span className="text-gray-900 font-semibold">{displayVal}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  const secQs = questions.filter(q => q.section === sec.id);
                  if (secQs.length === 0) return null;
                  
                  return (
                    <div key={sec.id} className="space-y-4 border-t border-gray-100 pt-6">
                      <h4 className="text-lg font-black text-emerald-800 uppercase tracking-tight">
                        {sec.label}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        {secQs.map(q => {
                          const val = viewingSurveyDetails[sec.id]?.[q.id] ?? viewingSurveyDetails[q.id];
                          if (val === undefined || val === null || val === '') {
                            return (
                              <div key={q.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col justify-between">
                                <span className="text-xs text-gray-500 font-medium leading-snug">{q.label}</span>
                                <span className="text-sm text-gray-400 italic mt-1">-</span>
                              </div>
                            );
                          }
                          
                          if (q.type === 'image') {
                            return (
                              <div key={q.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col justify-between">
                                <span className="text-xs text-gray-500 font-medium leading-snug block mb-2">{q.label}</span>
                                <div className="w-32 h-24 bg-gray-100 rounded overflow-hidden border border-gray-300">
                                  <img 
                                    src={val} 
                                    alt="Proof" 
                                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                    onClick={() => openPhotoViewer([val])}
                                  />
                                </div>
                              </div>
                            );
                          }

                          let displayVal = String(val);
                          if (q.type === 'yesno') {
                            displayVal = val === true || val === 'true' || val === 'Yes' || val === 'അതെ' ? 'Yes (അതെ)' : 'No (അല്ല)';
                          }

                          return (
                            <div key={q.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col justify-between">
                              <span className="text-xs text-gray-500 font-medium leading-snug">{q.label}</span>
                              <span className="text-sm text-gray-800 font-bold mt-1">{displayVal}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              })}
            </div>

            {/* Footer Actions */}
            <div className="bg-gray-50 p-4 border-t border-gray-150 flex justify-end flex-shrink-0">
              <button 
                onClick={() => setViewingSurveyDetails(null)} 
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded-lg font-bold transition-colors text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;